// Enclosing-`(` lead recognizers for the token DDL classifier — split out of
// `editorContextSlot.ddl2.ts` (plan19 T7 fix round, review Q3: keep the family
// file under the ~200-line budget along a clean seam). Each answers one
// question about the `(` the cursor sits inside: what construct opened it?
// G11: pure token scan, NO web-tree-sitter.

import type { EditorContextSlotKind } from '../model/editorContext';
import type { ClauseToken } from './editorContextClause';
import { headTableVerb } from './editorContextSlot.ddl';
import { findEnclosingLparenIndex, upper } from './editorContextSlotShared';

/** The `(` at `lparenIdx` is immediately preceded by `REFERENCES <tbl>`. */
export function precededByReferencesTarget(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  let i = lparenIdx - 1;
  if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
  i -= 1;
  if (i >= 0 && tokens[i]!.kind === 'dot') {
    i -= 1;
    if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
    i -= 1;
  }
  return upper(tokens[i]) === 'REFERENCES';
}

/** The `(` at `lparenIdx` is a `ON <tbl> [USING <method>] (` index-column
 *  opener, or the `INCLUDE (` covering-column list that follows one.
 *
 *  `INCLUDE` is part of the same statement's column vocabulary — its list
 *  names columns of the very same indexed table (T-SQL 2005+, PostgreSQL 11+)
 *  — so it answers with the same slot kind rather than falling through to the
 *  clause machine, which reads the paren as a FROM-tail and offered relations
 *  (mssql script.31 §5/§6). */
export function precededByIndexOn(tokens: readonly ClauseToken[], lparenIdx: number): boolean {
  if (upper(tokens[lparenIdx - 1]) === 'INCLUDE') return true;
  let i = lparenIdx - 1;
  // Optional Postgres `USING <method>` between the table and the `(`.
  if (i >= 1 && tokens[i]!.kind === 'identifier' && upper(tokens[i - 1]) === 'USING') i -= 2;
  if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
  i -= 1;
  if (i >= 0 && tokens[i]!.kind === 'dot') {
    i -= 1;
    if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
    i -= 1;
  }
  return upper(tokens[i]) === 'ON';
}

/** The `(` at `lparenIdx` is immediately preceded by `ON CONFLICT`. */
export function precededByOnConflict(tokens: readonly ClauseToken[], lparenIdx: number): boolean {
  return upper(tokens[lparenIdx - 1]) === 'CONFLICT' && upper(tokens[lparenIdx - 2]) === 'ON';
}

// Partition-method names that may sit between `BY` and the `(` opener
// (`PARTITION BY RANGE (`, `PARTITION BY HASH (`, MySQL's `LINEAR KEY (` /
// `RANGE COLUMNS (`, Oracle's `LIST (`).
const PARTITION_METHODS: ReadonlySet<string> = new Set([
  'HASH', 'RANGE', 'LIST', 'KEY', 'LINEAR', 'COLUMNS',
]);

/**
 * The `(` at `lparenIdx` is a `PARTITION BY [<method>] (` partition-expression
 * opener. PG, MySQL and Oracle all spell declarative partitioning this way, so
 * the recognizer lives in the cross-engine DDL family rather than in the mysql
 * residual file where it first landed (GRA8-ROLL-2) — SQLite has no
 * partitioning and simply never matches.
 */
export function precededByPartitionBy(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  let i = lparenIdx - 1;
  // Tolerate a chain of method tokens (`LINEAR KEY`, `RANGE COLUMNS`, `HASH`).
  while (i >= 0) {
    const u = upper(tokens[i]);
    if (u !== null && PARTITION_METHODS.has(u)) {
      i -= 1;
      continue;
    }
    break;
  }
  if (upper(tokens[i]) !== 'BY') return false;
  return upper(tokens[i - 1]) === 'PARTITION';
}

/**
 * The `(` at `lparenIdx` is a bare secondary-index column-list opener:
 * `INDEX (`, `INDEX ix_name (`, `KEY (`, `KEY ix_name (`. A `KEY` whose own
 * prior keyword is PRIMARY / UNIQUE / FOREIGN is a CONSTRAINT lead owned by
 * `ddlConstraintLead` and is excluded here so the two paths never collide.
 *
 * MySQL is the only one of the five dialects that accepts an inline index
 * declaration in a CREATE TABLE body or an `ALTER TABLE … ADD INDEX ix (…)`;
 * the others spell it `CREATE INDEX` / `ADD CONSTRAINT … UNIQUE`, both already
 * owned by other members of this family. The recognizer is dialect-neutral all
 * the same — nothing in the other four grammars can reach the shape.
 */
export function precededByBareIndexOrKey(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  // `INDEX` lexes as a keyword and `KEY` as an identifier, so probe by
  // uppercased text. The token before `(` is either the lead itself
  // (`INDEX (`) or an index-name identifier whose prior token is the lead.
  let i = lparenIdx - 1;
  if (isIndexLead(upper(tokens[i]))) return notOwnedKeyLead(tokens, i);
  if (i >= 0 && tokens[i]!.kind === 'identifier') {
    i -= 1;
    if (isIndexLead(upper(tokens[i]))) return notOwnedKeyLead(tokens, i);
  }
  return false;
}

function isIndexLead(u: string | null): boolean {
  return u === 'INDEX' || u === 'KEY';
}

/** Index-type prefixes that mark the opener as a constraint the
 *  `ddlConstraintLead` path owns — the slot must NOT double-classify. */
const OWNED_KEY_LEADS: ReadonlySet<string> = new Set(['PRIMARY', 'UNIQUE', 'FOREIGN']);

function notOwnedKeyLead(tokens: readonly ClauseToken[], leadIdx: number): boolean {
  const before = upper(tokens[leadIdx - 1]);
  return before === null || !OWNED_KEY_LEADS.has(before);
}

/**
 * The `(` at `lparenIdx` opens a VIRTUAL TABLE module's argument list —
 * `CREATE VIRTUAL TABLE t USING fts5(▮`. The arguments are the FTS table's own
 * column names, and an fts5 table that mirrors a real one names its source in
 * a `content='<tbl>'` option AFTER the column list, so the slot maps to
 * `'ddl-target-table-column'` (the resolver looks ahead for that option).
 *
 * `CREATE VIRTUAL TABLE … USING <module>(…)` is SQLite-only; the four other
 * grammars have no virtual tables, so the head gate makes the shape
 * unreachable for them. PG's `CREATE INDEX … USING gin (` is a different
 * construct entirely and is claimed earlier by `precededByIndexOn`.
 */
export function precededByVirtualTableUsing(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  const moduleName = tokens[lparenIdx - 1];
  if (!moduleName || moduleName.kind !== 'identifier') return false;
  if (upper(tokens[lparenIdx - 2]) !== 'USING') return false;
  for (let i = 0; i < lparenIdx; i += 1) {
    if (upper(tokens[i]) !== 'CREATE') continue;
    if (upper(tokens[i + 1]) === 'VIRTUAL' && upper(tokens[i + 2]) === 'TABLE') return true;
  }
  return false;
}

/**
 * The `PARTITION BY (▮` / bare `INDEX|KEY [name] (▮` pair, resolved to the
 * column-list kind their enclosing statement calls for: an in-progress CREATE
 * TABLE body answers with the columns declared so far, an ALTER TABLE with the
 * target's live columns. `null` when the caret is in neither shape.
 *
 * Shared entry point so `classifyDdlSlot` (postgres / mysql / oracle / sqlite)
 * and the mssql arm — which runs its own narrower `ddlConstraintSlot` and
 * never calls the family — give the same answer at the same caret. T-SQL 2014+
 * accepts the inline `INDEX ix (col)` form in a CREATE TABLE body; its
 * partitioning is spelled `ON <scheme>(col)` instead, which simply never
 * matches `PARTITION BY`.
 */
export function indexOrPartitionColumnListSlot(
  tokens: readonly ClauseToken[],
): EditorContextSlotKind | null {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return null;
  if (!precededByPartitionBy(tokens, lparenIdx) && !precededByBareIndexOrKey(tokens, lparenIdx)) {
    return null;
  }
  const head = headTableVerb(tokens);
  if (head === 'create') return 'ddl-create-table-expression';
  if (head === 'alter') return 'ddl-alter-table-constraint-column-list';
  return null;
}

/** Constraint keyword immediately before the `(`: CHECK / PK|FK|UNIQUE / GENERATED AS. */
export function ddlConstraintLead(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): 'check' | 'key-or-unique' | 'generated' | null {
  const last = upper(tokens[lparenIdx - 1]);
  if (last === 'CHECK') return 'check';
  if (last === 'UNIQUE') return 'key-or-unique';
  if (last === 'KEY') {
    const before = upper(tokens[lparenIdx - 2]);
    if (before === 'PRIMARY' || before === 'FOREIGN') return 'key-or-unique';
    return null;
  }
  if (last === 'AS') {
    // GENERATED (ALWAYS | BY DEFAULT) AS (▮
    let i = lparenIdx - 2;
    const u = upper(tokens[i]);
    if (u === 'ALWAYS') i -= 1;
    else if (u === 'DEFAULT') {
      i -= 1;
      if (upper(tokens[i]) !== 'BY') return null;
      i -= 1;
    } else return null;
    if (upper(tokens[i]) === 'GENERATED') return 'generated';
  }
  return null;
}
